/* eslint-disable no-console */
import {
  types,
  flow,
  getRoot,
  applySnapshot,
  getSnapshot
} from "mobx-state-tree";
import { values } from "mobx";
import { saveAs } from "file-saver";
import Papa from "papaparse";
import JSZip from "jszip";
import moment from "moment";

import { Aggregation } from "./AggregationStore";
import { ApiStore } from "./ApiStore";
import { SensorGroup } from "./SensorGroupStore";
import { Timeline } from "./Timeline";
import GenericJSONPrimitive from "./GenericJSONPrimitive";
import { Sensor } from "./SensorStore";

const Location = types.model({
  id: types.identifier,
  human_name: types.optional(types.string, "")
});

const Variety = types.model({
  id: types.identifier,
  human_name: types.optional(types.string, "")
});

const Room = types.compose(
  ApiStore,
  types
    .model({
      id: types.identifier,
      human_name: types.optional(types.string, ""),
      sensor_groups: types.array(
        types.late(() => types.reference(SensorGroup))
      ),
      potential_sensor_groups: types.array(
        types.late(() => types.reference(SensorGroup))
      ),
      readings: types.array(types.late(() => Aggregation)),
      locations: types.array(types.late(() => Location)),
      varieties: types.array(types.late(() => Variety)),
      sensors: types.array(types.late(() => Sensor)),
      meta: types.optional(GenericJSONPrimitive, "{}"),
      status: types.optional(types.string, ""),
      ppm: types.maybeNull(types.number),
      bins: types.maybeNull(types.number),
      notes: types.optional(types.string, ""),
      customer_notes: types.optional(types.string, ""),
      close_timestamp: types.maybeNull(types.string, ""),
      open_timestamp: types.maybeNull(types.string, ""),
      order: types.maybeNull(types.number),
      predicted_ready_timestamp: types.maybeNull(types.string, ""),
      timeline: types.maybeNull(Timeline)
    })
    .actions(self => ({
      pullReadings: flow(function* pullReadings() {
        const notifications = getRoot(self).notifications();

        try {
          const res = yield self.fetch2(`/rooms/${self.id}/readings/`);
          const json = yield res.json();

          self.readings.clear();
          self.readings = json;

          yield Promise.resolve();
        } catch (err) {
          console.error("Failed to fetch and update room readings.\n", err);
          notifications.error("Unknown error occured.");
        }
      }),

      pullSensors: flow(function* pullSensors() {
        const notifications = getRoot(self).notifications();
        try {
          const res = yield self.fetch2(`/rooms/${self.id}/sensors/`);
          const json = yield res.json();

          const filteredSensors = json.filter(
            sensor => !sensor.human_name.includes("c2h4_array")
          );
          self.sensors = filteredSensors;

          yield Promise.resolve();
        } catch (err) {
          console.error("Failed to fetch and update room sensors.\n", err);
          notifications.error("Unknown error occured.");
        }
      }),

      pullSensorGroups: flow(function* pullSensorGroups() {
        const notifications = getRoot(self).notifications();

        try {
          const res = yield self.fetch2(`/rooms/${self.id}/sensor_groups/`);
          const json = yield res.json();

          getRoot(self).sensorGroupStore.update(json);

          self.sensor_groups.clear();
          json.forEach(sg => {
            self.sensor_groups.push(sg.id);
          });

          yield Promise.resolve();
        } catch (err) {
          console.error("Failed to fetch and update sensor groups.\n", err);
          notifications.error("Unknown error occured.");
        }
      }),

      pullPotentialSensorGroups: flow(function* pullSensorGroups() {
        const notifications = getRoot(self).notifications();

        try {
          const res = yield self.fetch2(
            `/rooms/${self.id}/potential_sensor_groups/`
          );
          const json = yield res.json();

          getRoot(self).sensorGroupStore.update(json);

          self.potential_sensor_groups.clear();
          json.forEach(sg => {
            self.potential_sensor_groups.push(sg.id);
          });

          yield Promise.resolve();
        } catch (err) {
          console.error(
            "Failed to fetch and update potential sensor groups.\n",
            err
          );
          notifications.error("Unknown error occured.");
        }
      }),

      partialUpdate: flow(function* partialUpdate(updatedJson) {
        const notifications = getRoot(self).notifications();

        try {
          const updated = {
            ...updatedJson
          };

          const res = yield self.fetch2(`/rooms/${self.id}/`, {
            method: "PATCH",
            body: JSON.stringify(updated)
          });
          const json = yield res.json();

          const combinedSnapshot = {
            ...getSnapshot(self),
            ...json
          };
          applySnapshot(self, combinedSnapshot);
        } catch (err) {
          console.error("Failed to update room.\n", err);
          notifications.error("Unknown error occured.");
        }
      }),

      updateSensorGroups: flow(function* updateSensorGroups(sensorGroupsJson) {
        // FIXME: This should update the sensor groups themselves with any
        // updated info in addition to just updating the IDs.
        self.sensor_groups = sensorGroupsJson.map(sg => sg.id);
        yield Promise.resolve();
      }),

      addSensorGroupsByIds: flow(function* addSensorGroupsByIds(
        sensorGroupIds
      ) {
        const notifications = getRoot(self).notifications();

        try {
          const res = yield self.fetch2(`/rooms/${self.id}/sensor_groups/`, {
            method: "POST",
            body: JSON.stringify(sensorGroupIds)
          });
          const json = yield res.json();

          getRoot(self).sensorGroupStore.update(json);

          self.sensor_groups.clear();
          json.forEach(sg => {
            self.sensor_groups.push(sg.id);
          });

          yield Promise.resolve();
        } catch (err) {
          console.error("Failed to add sensor groups.\n", err);
          notifications.error("Unknown error occured.");
        }
      }),

      removeSensorGroupsByIds: flow(function* removeSensorGroupsByIds(
        sensorGroupIds
      ) {
        const notifications = getRoot(self).notifications();

        try {
          const res = yield self.fetch2(`/rooms/${self.id}/sensor_groups/`, {
            method: "DELETE",
            body: JSON.stringify(sensorGroupIds)
          });
          const json = yield res.json();

          getRoot(self).sensorGroupStore.update(json);

          self.sensor_groups.clear();
          json.forEach(sg => {
            self.sensor_groups.push(sg.id);
          });

          yield Promise.resolve();
        } catch (err) {
          console.error("Failed to remove sensor groups.\n", err);
          notifications.error("Unknown error occured.");
        }
      }),

      downloadJson: flow(function* downloadJson() {
        yield self.fetchAndUpdateReadings().then(() => {
          const readings = self.orderedReadings();
          const json = JSON.stringify(readings);
          const blob = new Blob([json], { type: "application/json" });

          const locationname = self.locations.length
            ? self.locations[0].human_name
            : "";
          const roomname = self.human_name;
          saveAs(
            blob,
            `${locationname} ${roomname}.json`.replace(/[^a-z0-9.]/gi, "_")
          );
        });
      }),

      downloadCsv: flow(function* downloadCsv() {
        yield self.fetchAndUpdateReadings().then(() => {
          const flatReadings = self.orderedReadings().map(r => ({
            id: r.id,
            timestamp: moment(r.timestamp).format("LLL"),
            ppm: r.payload.ppm
          }));

          const csv = Papa.unparse(flatReadings);
          const blob = new Blob([csv], { type: "application/csv" });

          const locationname = self.locations.length
            ? self.locations[0].human_name
            : "";
          const roomname = self.human_name;
          saveAs(
            blob,
            `${locationname} ${roomname}.csv`.replace(/[^a-z0-9.]/gi, "_")
          );
        });
      }),

      calibrateRoom: flow(function* calibrateRoom({ start_timestamp, slope }) {
        const notifications = getRoot(self).notifications();

        const res = yield self.fetch2(`/rooms/${self.id}/calibrate/`, {
          method: "PUT",
          body: JSON.stringify({
            slope,
            start_timestamp
          })
        });

        if (res.status === 204) {
          notifications.success(
            `Room ${self.human_name} successfully calibrated!`
          );
        } else {
          const error = yield res.json();
          console.error({ ...error });

          notifications.error("Failed to calibrate room");
          throw new Error("Failed to calibrate room.");
        }
      })
    }))
    .views(self => ({
      orderedReadings() {
        const readings = getSnapshot(self.readings).filter(
          r =>
            (self.open_timestamp ? r.timestamp < self.open_timestamp : true) &&
            (self.close_timestamp ? r.timestamp > self.close_timestamp : true)
        );

        return readings;
      }
    }))
);

const RoomStore = types.compose(
  ApiStore,
  types
    .model({
      rooms: types.map(Room)
    })
    .actions(self => ({
      bootstrap: flow(function* bootstrap() {
        yield self.pull();
      }),

      pull: flow(function* pull() {
        const notifications = getRoot(self).notifications();

        try {
          const res = yield self.fetch2("/rooms/");
          const json = yield res.json();

          self.rooms.clear();
          json.forEach(room => {
            self.rooms.put(room);
          });

          yield Promise.resolve();
        } catch (err) {
          console.error("Failed to fetch and update rooms.\n", err);
          notifications.error("Unknown error occured.");
        }
      }),

      update: flow(function* update(roomsJson) {
        self.rooms.clear();
        roomsJson.forEach(roomJson => {
          self.rooms.put(roomJson);
        });
        yield Promise.resolve();
      }),

      fetchAndUpdateAllReadings: flow(function* fetchAndUpdateAllReadings() {
        const rooms = Array.from(self.rooms.keys()).map(k => self.rooms.get(k));
        const updates = rooms.map(r => r.fetchAndUpdateReadings());
        yield Promise.all(updates);
      }),

      downloadJson: flow(function* downloadJson() {
        yield self.fetchAndUpdateAllReadings().then(() => {
          let zip = new JSZip();

          self.rooms.forEach(r => {
            const readings = r.orderedReadings();
            const json = JSON.stringify(readings);

            const locationname = r.locations.length
              ? r.locations[0].human_name
              : "";
            const roomname = r.human_name;

            zip.file(
              `${locationname} ${roomname}.json`.replace(/[^a-z0-9.]/gi, "_"),
              json
            );
          });

          zip.generateAsync({ type: "blob" }).then(blob => {
            saveAs(blob, `Room Readings ${moment().format()}.zip`);
          });
        });
      }),

      downloadByLocationCsv: flow(function* downloadByLocationCsv() {
        yield self.fetchAndUpdateAllReadings().then(() => {
          let zip = new JSZip();

          self.locations().forEach(l => {
            const roomKeys = Array.from(self.rooms.keys());
            const rooms = roomKeys
              .map(k => self.rooms.get(k))
              .sort((a, b) => a.human_name.localeCompare(b.human_name))
              .filter(r => r.locations.map(l => l.id).includes(l.id));

            // Map by date to map by sensor to array of readings
            let groupedReadings = new Map();

            rooms.forEach(r => {
              const readingKeys = Array.from(r.readings.keys());
              readingKeys
                .map(k => r.readings.get(k))
                .forEach(re => {
                  const date = moment(re.timestamp).format("L");

                  if (!groupedReadings.has(date)) {
                    groupedReadings.set(date, new Map());
                  }

                  if (!groupedReadings.get(date).has(r.id)) {
                    groupedReadings.get(date).set(r.id, []);
                  }

                  groupedReadings
                    .get(date)
                    .get(r.id)
                    .push(re);
                });
            });

            // Pick the first aggregation per room per day
            for (const date of groupedReadings.keys()) {
              for (const roomId of groupedReadings.get(date).keys()) {
                const readings = groupedReadings.get(date).get(roomId);
                groupedReadings.get(date).set(roomId, readings[0]);
              }
            }

            // Make it into a CSV
            const fields = ["date"].concat(rooms.map(r => r.human_name));
            const data = [];

            Array.from(groupedReadings.keys()).forEach(k => {
              let dayData = [];
              dayData.push(k);

              rooms.forEach(r => {
                dayData.push(
                  ((groupedReadings.get(k).get(r.id) || {}).payload || {})
                    .ppm || ""
                );
              });

              data.push(dayData);
            });

            const csv = Papa.unparse({ fields: fields, data: data });
            zip.file(
              `${l.human_name || l.id}.csv`.replace(/[^a-z0-9.]/gi, "_"),
              csv
            );
          });

          zip.generateAsync({ type: "blob" }).then(blob => {
            saveAs(
              blob,
              `Location Aggregated Room Readings ${moment().format()}.zip`
            );
          });
        });
      }),

      downloadCsv: flow(function* downloadCsv() {
        yield self.fetchAndUpdateAllReadings().then(() => {
          let zip = new JSZip();

          self.rooms.forEach(room => {
            const flatReadings = room.orderedReadings().map(r => ({
              id: r.id,
              timestamp: moment(r.timestamp).format("LLL"),
              ppm: r.payload.ppm
            }));

            const csv = Papa.unparse(flatReadings);

            const locationname = room.locations.length
              ? room.locations[0].human_name
              : "";
            const roomname = room.human_name;

            zip.file(
              `${locationname} ${roomname}.csv`.replace(/[^a-z0-9.]/gi, "_"),
              csv
            );
          });

          zip.generateAsync({ type: "blob" }).then(blob => {
            saveAs(blob, `Room Readings ${moment().format()}.zip`);
          });
        });
      }),

      delete: flow(function* deleteRoom(roomId) {
        yield self.fetch(
          `/rooms/${roomId}/`,
          {
            method: "DELETE"
          },
          self.update,
          err => {
            console.error("Failed to delete room.\n", err);
          }
        );
      }),

      reset: flow(function* reset() {
        applySnapshot(self, {});
        yield Promise.resolve();
      })
    }))
    .views(self => ({
      ordered() {
        return values(self.rooms).sort((a, b) => {
          if (a.status === "open" && b.status === "open") {
            if (a.open_timestamp !== null && b.open_timestamp !== null) {
              return a.open_timestamp.localeCompare(b.open_timestamp);
            }
            if (a.open_timestamp == null && b.open_timestamp == null) {
              return a.order - b.order;
            }
            // if a has open timestamp, it should come before b
            return a.open_timestamp !== null ? -1 : 1;
          }
          if (a.status !== "open" && b.status !== "open") {
            if (
              a.predicted_ready_timestamp !== null &&
              b.predicted_ready_timestamp !== null
            ) {
              return a.predicted_ready_timestamp.localeCompare(
                b.predicted_ready_timestamp
              );
            }
            if (
              a.predicted_ready_timestamp == null &&
              b.predicted_ready_timestamp == null
            ) {
              return a.order - b.order;
            }
            // if a has predicted ready, it should come before b
            return a.predicted_ready_timestamp !== null ? -1 : 1;
          }
          if (a.status === "open" && b.status !== "open") {
            return 1; // b is not open, should come first
          }
          if (a.status !== "open" && b.status === "open") {
            return -1; // a is not open, should come first
          }
          return 0; // sort compareFunction expects a return statement
        });
      },

      locations() {
        let locations = [];
        let existingIds = new Set();

        values(self.rooms)
          .map(r => r.locations)
          .flat()
          .forEach(l => {
            if (!existingIds.has(l.id)) {
              existingIds.add(l.id);
              locations.push(l);
            }
          });

        return locations.sort((a, b) =>
          a.human_name.localeCompare(b.human_name)
        );
      },

      locationsForSeasons(seasons) {
        let locations = [];
        let existingIds = new Set();
        const seasonsSet = new Set(seasons);

        values(self.rooms)
          .filter(r => seasonsSet.has(r.meta.season))
          .map(r => r.locations)
          .flat()
          .forEach(l => {
            if (!existingIds.has(l.id)) {
              existingIds.add(l.id);
              locations.push(l);
            }
          });

        return locations.sort((a, b) =>
          a.human_name.localeCompare(b.human_name)
        );
      },

      varieties() {
        let varieties = [];
        let existingIds = new Set();

        values(self.rooms)
          .map(r => r.varieties)
          .flat()
          .forEach(v => {
            if (!existingIds.has(v.id)) {
              existingIds.add(v.id);
              varieties.push(v);
            }
          });

        return varieties.sort((a, b) =>
          a.human_name.localeCompare(b.human_name)
        );
      },

      varietiesForSeasons(seasons) {
        let varieties = [];
        let existingIds = new Set();
        const seasonsSet = new Set(seasons);

        values(self.rooms)
          .filter(r => seasonsSet.has(r.meta.season))
          .map(r => r.varieties)
          .flat()
          .forEach(v => {
            if (!existingIds.has(v.id)) {
              existingIds.add(v.id);
              varieties.push(v);
            }
          });

        return varieties.sort((a, b) =>
          a.human_name.localeCompare(b.human_name)
        );
      },

      seasons() {
        let seasons = new Set();
        values(self.rooms)
          .map(r => r.meta)
          .forEach(v => {
            seasons.add(v.season);
          });
        return Array.from(seasons);
      }
    }))
);

export { Room, RoomStore };
